import { OperationVariables, QueryResult } from '@apollo/client';
import { captureMessage } from '@sentry/react';
import { ReactElement, ReactNode, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import styles from '@components/HandleQuery.module.css';
import Icon from '@components/text/Icon';
import LargeText from '@components/text/LargeText';

type Props<TData, TVariables extends OperationVariables> = {
  children: (data: Exclude<QueryResult<TData, TVariables>['data'], undefined>) => ReactElement;
  extraLoading?: boolean;
  hideLoader?: boolean;
  loadingContent?: ReactNode;
  query: QueryResult<TData, TVariables>;
};

const HandleQuery = <TData, TVariables extends OperationVariables>({
  children,
  extraLoading = false,
  hideLoader = false,
  loadingContent,
  query
}: Props<TData, TVariables>) => {
  useEffect(() => {
    if (query.error) {
      captureMessage(
        `Unexpected error: ${query.error.message} -- GQLERRORS ${JSON.stringify(
          query.error.graphQLErrors
        )} -- NETERROR ${JSON.stringify(query.error.networkError)} -- EXTRAINFO ${JSON.stringify(
          query.error.extraInfo
        )} -- DATA ${JSON.stringify(query.data)} -- CLIENTDATA ${JSON.stringify(query.error.clientErrors)}`,
        { level: 'error' }
      );
    }
  }, [query.data, query.error]);

  if ((query.loading || extraLoading) && !hideLoader) {
    if (loadingContent) return <div>{loadingContent}</div>;

    return (
      <div className={styles.center}>
        <Icon icon="spinner" size="xlarge" />
      </div>
    );
  }

  if (query.error) {
    if (query.error.message === 'unauthorized') {
      return (
        <div className={styles.center}>
          <LargeText>
            <FormattedMessage id="errors.handleQuery.unauthorized" />
          </LargeText>
        </div>
      );
    }
    if (query.error.message === 'not_found') {
      return (
        <div className={styles.center}>
          <LargeText>
            <FormattedMessage id="errors.handleQuery.notFound" />
          </LargeText>
        </div>
      );
    }
    return (
      <div className={styles.center}>
        <LargeText>
          <FormattedMessage id="errors.handleQuery.somethingWentWrong" />
        </LargeText>
      </div>
    );
  }

  if (!query.data && !hideLoader) {
    return (
      <div className={styles.center}>
        <Icon icon="spinner" size="xlarge" />
      </div>
    );
  }

  if (!query.data && hideLoader) {
    return null;
  }

  return <>{children(query.data as Exclude<TData, undefined>)}</>;
};

export default HandleQuery;
